import { useState } from "react";
import { useTranslation } from "react-i18next";
import { vehicleCategories, vehicles } from "@/mocks/vehicles";
import { waLink } from "@/lib/links";
import SectionHeading from "@/components/feature/SectionHeading";

export default function FleetSection() {
  const { t } = useTranslation();
  const [active, setActive] = useState("all");
  const filtered = active === "all" ? vehicles : vehicles.filter((v) => v.category === active);

  return (
    <section id="fleet" className="py-20 md:py-28 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <SectionHeading
            align="left"
            eyebrow={t("fleet.eyebrow")}
            title={
              <>
                {t("fleet.title1")}
                <br />
                {t("fleet.title2")}
              </>
            }
          />
          <p className="text-foreground-500 text-sm md:text-base leading-relaxed max-w-md lg:pb-2">
            {t("fleet.subtitle")}
          </p>
        </div>

        <div className="flex flex-wrap gap-1 mb-10 bg-background-200 p-1 rounded-full w-fit no-scrollbar">
          {vehicleCategories.map((category) => (
            <button
              key={category.id}
              type="button"
              onClick={() => setActive(category.id)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition ${
                active === category.id
                  ? "bg-foreground-900 text-background-50"
                  : "text-foreground-600 hover:text-foreground-900"
              }`}
            >
              <i className={`${category.icon} w-4 h-4 flex items-center justify-center`} />
              {t(`fleet.categories.${category.id}`)}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6" data-product-shop="true">
          {filtered.map((vehicle) => (
            <article
              key={vehicle.id}
              className="group bg-background-100 border border-background-200 rounded-lg overflow-hidden hover:border-primary-300 transition"
            >
              <div className="relative h-56 overflow-hidden bg-background-50">
                <img
                  alt={vehicle.name}
                  className="w-full h-full object-contain group-hover:scale-105 transition duration-500"
                  src={vehicle.image}
                />
                <span className="absolute top-4 right-4 bg-background-50/95 backdrop-blur text-foreground-900 text-xs font-bold px-3 py-1.5 rounded-full">
                  {t(`fleet.categories.${vehicle.category}`)}
                </span>
              </div>
              <div className="p-6">
                <h3 className="font-heading text-lg font-bold text-foreground-900">{vehicle.name}</h3>
                <p className="mt-1 text-primary-700 text-sm font-semibold">{t(`fleet.badges.${vehicle.badgeKey}`)}</p>

                <ul className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-foreground-500 text-xs font-medium">
                  <li className="flex items-center gap-1.5">
                    <i className="ri-gas-station-fill text-primary-500 w-4 h-4 flex items-center justify-center" />
                    {t("fleet.fuelPetrol", { value: vehicle.fuel })}
                  </li>
                  <li className="flex items-center gap-1.5">
                    <i className="ri-user-fill text-primary-500 w-4 h-4 flex items-center justify-center" />
                    {t("fleet.seats", { count: vehicle.seats })}
                  </li>
                </ul>

                <div className="mt-6 flex items-center justify-between gap-3 pt-5 border-t border-background-200">
                  <div>
                    <div className="text-[11px] text-foreground-400 uppercase tracking-wider font-semibold">
                      {t("fleet.weeklyRate")}
                    </div>
                    <div className="font-heading font-extrabold text-foreground-900 text-base md:text-lg">
                      {t("fleet.perWeek", { price: vehicle.priceValue })}
                    </div>
                  </div>
                  <a
                    href={waLink(t("wa.fleet", { vehicle: vehicle.name }))}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-foreground-900 hover:bg-primary-500 text-background-50 text-sm font-semibold px-4 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition"
                  >
                    <i className="ri-whatsapp-line w-4 h-4 flex items-center justify-center" />
                    {t("fleet.lease")}
                  </a>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}